/**
 * MenuBar — The main bottom navigation pill of the 3D Viewer.
 *
 * A centered glass-morphism pill that holds all feature-flag-driven
 * action buttons. Buttons come in three distinct types:
 *
 * ┌─────────────────────────────────────────────────────────────────┐
 * │  Button type        Component          Behaviour                │
 * ├─────────────────────────────────────────────────────────────────┤
 * │  Submenu toggle     MenuButton         Opens/closes SubMenuPanel │
 * │  Mode toggle        MenuToggleButton   Binary state, icon swap   │
 * │  Action / overlay   MenuButton         Opens overlay screen      │
 * └─────────────────────────────────────────────────────────────────┘
 *
 * Button order:
 *   1. 💧 Color         MenuButton       — exteriorColors flag
 *   2. 🔩 Rims          MenuButton       — rimSelector flag   (placeholder)
 *   3. 🌍↔🏢 Studio     MenuToggleButton — environmentSwitch flag
 *   4. 🚪 Door anim     MenuToggleButton — doorAnimation flag  (placeholder)
 *   5. 🌞↔🌙 Day/Night  MenuToggleButton — environmentSwitch flag
 *
 * The Info / CTA button has been replaced by the floating CtaPanel component
 * (right edge of screen). @see CtaPanel.tsx
 *
 * Animation when a SubMenuPanel opens:
 *   Pill scales down (0.78) and moves up (-77px) via CSS transform.
 *   Outer wrapper handles only translateX(-50%) centering so the %
 *   never participates in the animated transform → no sub-pixel drift.
 *
 * @see docs/ims-brand-frontends/08-ui-screens.md#bottom-menu-bar
 */
import { useCallback, useRef, useState, useEffect, type ReactNode } from 'react';
import { applyScene } from '@ims/engine';
import type { Engine } from '@ims/engine';
import { useAppStore, PANEL_SUBMENUS } from '../../store/app.store';
import { useUiTheme } from '../../hooks/useUiTheme';
import { tracker, EVENTS } from '../../services/tracking';
import { MenuButton } from './MenuButton';
import { MenuToggleButton } from './MenuToggleButton';
import type { ResolvedConfig } from '@ims/shared';

// ---------------------------------------------------------------------------
// Icon paths — served from /public/app/icons/
// ---------------------------------------------------------------------------
const ICON_COLOR = '/app/icons/audi-icon-colorMenu.svg';
const ICON_RIM = '/app/icons/rimsMenu-icon.svg'; // TODO: replace with dedicated rim icon
const ICON_WORLD = '/app/icons/audi-icon-world.svg'; // studio toggle — road state
const ICON_STUDIO = '/app/icons/audi-icon-studio.svg'; // studio toggle — studio state
const ICON_DOOR_CLOSED = '/app/icons/audi-icon-openDoor.svg'; // door toggle — closed state (icon shows action)
const ICON_DOOR_OPEN = '/app/icons/audi-icon-closeDoor.svg'; // door toggle — open state
const ICON_DAY = '/app/icons/nightMode-icon.svg'; // day/night toggle — day state
const ICON_NIGHT = '/app/icons/dayMode-icon.svg'; // day/night toggle — night state

// ---------------------------------------------------------------------------
// MenuBarSlot — animated wrapper for conditional buttons
//
// Smoothly scales a button slot in/out when a feature becomes available or
// unavailable during a session (e.g. Day/Night hidden in Studio mode).
// The pill width transitions naturally as slots appear/disappear.
// ---------------------------------------------------------------------------
function MenuBarSlot({
  visible,
  noTrail,
  children,
}: {
  visible: boolean;
  noTrail?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        width: visible ? '44px' : '0px',
        marginRight: visible && !noTrail ? '12px' : '0px',
        opacity: visible ? 1 : 0,
        transform: visible ? 'scale(1)' : 'scale(0.5)',
        overflow: 'hidden',
        flexShrink: 0,
        transition: [
          'width 350ms cubic-bezier(0.32, 0.72, 0, 1)',
          'margin-right 350ms cubic-bezier(0.32, 0.72, 0, 1)',
          'opacity 250ms ease',
          'transform 350ms cubic-bezier(0.32, 0.72, 0, 1)',
        ].join(', '),
      }}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface MenuBarProps {
  config: ResolvedConfig | null;
  sceneReady: boolean;
  engineRef: React.RefObject<Engine | null>;
  /** When true, hides the bar immediately; on false restores it after the pano curtain clears (~900ms). */
  hidden?: boolean;
}

export function MenuBar({ config, sceneReady, engineRef, hidden = false }: MenuBarProps) {
  const activeSubMenu = useAppStore((s) => s.activeSubMenu);
  const activeScene = useAppStore((s) => s.activeScene);
  const doorsOpen = useAppStore((s) => s.doorsOpen);
  const setActiveScene = useAppStore((s) => s.setActiveScene);
  const setDoorsOpen = useAppStore((s) => s.setDoorsOpen);
  const theme = useUiTheme();

  // When hidden (interior mode), disappear instantly behind the engine curtain.
  // When revealed again, delay 900ms (fade-out + load + fade-in) so it slides
  // in only after the pano curtain has lifted.
  const [curtainCleared, setCurtainCleared] = useState(!hidden);
  useEffect(() => {
    if (hidden) {
      setCurtainCleared(false);
      return;
    }
    const t = setTimeout(() => setCurtainCleared(true), 900);
    return () => clearTimeout(t);
  }, [hidden]);

  const isPanelOpen = PANEL_SUBMENUS.has(activeSubMenu);
  const isStudioMode = activeScene === 'studio';
  const isNightMode = activeScene === 'night';
  // Night toggle only available on road scenes (not studio)
  const isRoadScene = !isStudioMode;

  // Remembers whether the user was on 'day' or 'night' before entering studio,
  // so we can restore it when they switch back.
  const lastRoadSceneRef = useRef<string>('day');
  // eslint-disable-next-line react-hooks/refs -- intentional: tracks last road scene synchronously during render for imperative restore
  if (isRoadScene) lastRoadSceneRef.current = activeScene;

  // ── Toggle handlers ───────────────────────────────────────────────────────
  const handleStudioToggle = useCallback(() => {
    const scenes = config?.model.scenes;
    if (!scenes) return;
    // Leaving studio → restore what the user had before (day or night)
    const targetId = isStudioMode ? lastRoadSceneRef.current : 'studio';
    const sceneDef = scenes[targetId];
    if (!sceneDef) return;
    const engine = engineRef.current;
    if (engine) applyScene(engine, sceneDef);
    setActiveScene(targetId);
  }, [isStudioMode, config, engineRef, setActiveScene]);

  const handleDoorToggle = useCallback(() => {
    const engine = engineRef.current;
    const nextOpen = !doorsOpen;
    if (engine) {
      // Collect clip names from model config. Each AnimationDef.clipName is
      // a named GLB clip. Play them all forward (open) or reverse (close).
      // Falls back to '*' (all clips) when no animations are configured.
      const clips = config?.model.animations;
      const direction = nextOpen ? 'forward' : 'reverse';
      if (clips && clips.length > 0) {
        for (const anim of clips) {
          engine.playAnimation(anim.clipName, { direction });
        }
      } else {
        engine.playAnimation('*', { direction });
      }
    }
    setDoorsOpen(nextOpen);
    tracker.track(EVENTS.DOOR_OPENED, { state: nextOpen ? 'open' : 'close' });
  }, [doorsOpen, setDoorsOpen, engineRef, config]);

  const handleNightToggle = useCallback(() => {
    const scenes = config?.model.scenes;
    if (!scenes) return;
    const targetId = isNightMode ? 'day' : 'night';
    const sceneDef = scenes[targetId];
    if (!sceneDef) return;
    const engine = engineRef.current;
    if (engine) applyScene(engine, sceneDef);
    setActiveScene(targetId);
  }, [isNightMode, config, engineRef, setActiveScene]);

  // Which slots are currently visible — same order as JSX below.
  // lastVisible = index of the rightmost visible slot, so only that slot
  // gets noTrail, keeping both pill edges equal regardless of mode/model.
  const slotVisible = [
    Boolean(config?.features.exteriorColors), // 0 Color
    Boolean(config?.features.rimSelector), // 1 Rims
    Boolean(config?.features.doorAnimation), // 2 Door
    Boolean(config?.features.environmentSwitch), // 3 Studio
    Boolean(config?.features.environmentSwitch) && isRoadScene, // 4 Day/Night
  ];
  const lastVisible = slotVisible.reduceRight<number>(
    (acc, v, i) => (acc === -1 && v ? i : acc),
    -1,
  );

  return (
    // Outer: centering only — translateX(-50%) never animates, so % never
    // participates in the scale transform → no sub-pixel icon drift.
    <div className="absolute bottom-5 left-1/2 z-100" style={{ transform: 'translateX(-50%)' }}>
      <div
        className={`flex items-center h-13 px-2 rounded-full ${theme.pill} backdrop-blur-[10px] shadow-[0_4px_15px_rgba(0,0,0,0.6)]`}
        style={{
          transform: isPanelOpen ? 'translateY(-77px) scale(0.78)' : 'translateY(0px) scale(1)',
          // bg-color in transition so useUiTheme night-mode change animates
          transition: 'transform 350ms ease, background-color 700ms ease, opacity 400ms ease',
          opacity: sceneReady && curtainCleared ? 1 : 0,
          pointerEvents: sceneReady && curtainCleared ? 'auto' : 'none',
        }}
      >
        {/* 1 — Colors: submenu toggle ───────────────────────────────────── */}
        <MenuBarSlot visible={slotVisible[0]} noTrail={lastVisible === 0}>
          <MenuButton icon={ICON_COLOR} label="Select colour" submenuKey="color" />
        </MenuBarSlot>

        {/* 2 — Rims: submenu toggle (placeholder — icon + logic TBD) ───── */}
        <MenuBarSlot visible={slotVisible[1]} noTrail={lastVisible === 1}>
          <MenuButton icon={ICON_RIM} label="Select rims" submenuKey="rim" />
        </MenuBarSlot>

        {/* 4 — Door animation: mode toggle
            Closed → openDoor icon  (icon shows the available action)
            Open   → closeDoor icon (icon reflects current state)      */}
        <MenuBarSlot visible={slotVisible[2]} noTrail={lastVisible === 2}>
          <MenuToggleButton
            icon={ICON_DOOR_CLOSED}
            iconActive={ICON_DOOR_OPEN}
            label={doorsOpen ? 'Close doors' : 'Open doors'}
            isActive={doorsOpen}
            onClick={handleDoorToggle}
          />
        </MenuBarSlot>

        {/* 3 — Studio mode: mode toggle
            Road state   → world icon  (default, natural environment)
            Studio state → studio icon (controlled studio lighting)    */}
        <MenuBarSlot visible={slotVisible[3]} noTrail={lastVisible === 3}>
          <MenuToggleButton
            icon={ICON_WORLD}
            iconActive={ICON_STUDIO}
            label={isStudioMode ? 'Switch to outdoor environment' : 'Switch to studio'}
            isActive={isStudioMode}
            onClick={handleStudioToggle}
          />
        </MenuBarSlot>

        {/* 5 — Day / Night: mode toggle — only available on road scenes
            Day state   → lightOn icon
            Night state → lightOff icon                                */}
        <MenuBarSlot visible={slotVisible[4]} noTrail={lastVisible === 4}>
          <MenuToggleButton
            icon={ICON_DAY}
            iconActive={ICON_NIGHT}
            label={isNightMode ? 'Activate day mode' : 'Activate night mode'}
            isActive={isNightMode}
            onClick={handleNightToggle}
          />
        </MenuBarSlot>
      </div>
    </div>
  );
}
